import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateRoleRequestSchema,
  UpdateRoleRequestSchema,
  type CreateRoleRequest,
  type RoleDetail,
  type RoleListItem,
  type UpdateRoleRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateRole, useUpdateRole } from '../use-roles';

/// Convert a human-typed name like "QA Lead" into a canonical machine
/// code "qa_lead" matching CreateRoleRequestSchema's regex
/// `^[a-z][a-z0-9_]*$`. Trims, lowercases, collapses runs of non-alnum
/// characters to a single underscore, and strips a leading digit so the
/// first char is always a letter.
const slugifyCode = (name: string): string => {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[0-9]+/, '');
  return slug.slice(0, 40);
};

interface RoleFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  /// All roles in the system - used to populate the parent-role select.
  /// Filtered to exclude `role` itself (cycle guard).
  allRoles: ReadonlyArray<RoleListItem>;
  role?: RoleDetail | null;
}

export const RoleFormDrawer = ({
  open,
  onOpenChange,
  mode,
  allRoles,
  role,
}: RoleFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const pending = createRole.isPending || updateRole.isPending;

  // Radix's `<SelectItem value="">` is a hard runtime error - the empty
  // string is reserved for "no selection" / placeholder. Use a sentinel
  // for the "no parent" choice and translate to/from null at the field
  // boundary below.
  const PARENT_NONE = '__none__';
  const parentOptions = useMemo(() => {
    const excludeId = isCreate ? undefined : role?.id;
    return [
      { value: PARENT_NONE, label: 'No parent (root role)' },
      ...allRoles
        .filter((r) => r.id !== excludeId)
        .map((r) => ({
          value: r.id,
          label: `${r.name} (${r.code})`,
        })),
    ];
  }, [allRoles, isCreate, role?.id]);

  const createForm = useForm<CreateRoleRequest>({
    resolver: zodResolver(CreateRoleRequestSchema),
    defaultValues: {
      code: '',
      name: '',
      description: '',
      parentRoleId: null,
      sortOrder: 100,
    },
  });
  const updateForm = useForm<UpdateRoleRequest>({
    resolver: zodResolver(UpdateRoleRequestSchema),
    defaultValues: {},
  });

  /// Name → code auto-fill runs in the background; the code field is not
  /// shown on create - it is derived from the display name on save.
  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset({
        code: '',
        name: '',
        description: '',
        parentRoleId: null,
        sortOrder: 100,
      });
    } else if (role) {
      updateForm.reset({
        name: role.name,
        description: role.description ?? '',
        parentRoleId: role.parentRoleId,
        sortOrder: role.sortOrder,
      });
    }
  }, [open, isCreate, role, createForm, updateForm]);

  // Name → code (create only). Slugified in form state; not shown in the UI.
  const watchedName = createForm.watch('name');
  useEffect(() => {
    if (!isCreate) return;
    const next = slugifyCode(watchedName ?? '');
    if (createForm.getValues('code') !== next) {
      createForm.setValue('code', next, { shouldValidate: false });
    }
  }, [isCreate, watchedName, createForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    const payload: CreateRoleRequest = {
      ...values,
      description: values.description?.trim() ? values.description.trim() : null,
      parentRoleId: values.parentRoleId || null,
    };
    try {
      await createRole.mutateAsync(payload);
      onOpenChange(false);
    } catch {
      // useCreateRole.onError already toasts the message - keep the
      // drawer open so the operator can fix and retry without re-typing.
    }
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!role) return;
    const payload: UpdateRoleRequest = {
      name: values.name,
      description: values.description === '' ? null : values.description,
      parentRoleId: values.parentRoleId || null,
      sortOrder: values.sortOrder,
    };
    try {
      await updateRole.mutateAsync({ id: role.id, payload });
      onOpenChange(false);
    } catch {
      // Same rationale as onCreate above.
    }
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'New role' : `Edit ${role?.name ?? 'role'}`}
      description={
        isCreate
          ? 'A non-system role you can later assign to users. Permission grants happen on the role detail screen after save.'
          : role?.isSystem
            ? 'System role: name, description, parent, and sort order are editable; code and delete are locked.'
            : 'All fields are editable. Reassign or remove users before changing the parent.'
      }
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Create role' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          <UzFormField name="name">
            {({ field, error }) => (
              <UzInput
                {...field}
                label="Display name"
                placeholder="Regional Manager"
                hint="What users see in lists and assign-role pickers. The machine code is auto-generated from this name."
                error={error}
                required
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="description">
            {({ field, error }) => (
              <UzTextarea
                {...field}
                value={field.value ?? ''}
                label="Description"
                placeholder="What does this role cover?"
                rows={3}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="parentRoleId">
            {({ field, error }) => (
              <UzSelect<string>
                name={field.name}
                label="Parent role"
                hint="Inherits all permissions from the parent. Pick 'No parent' for a root role."
                options={parentOptions}
                value={field.value ?? PARENT_NONE}
                onValueChange={(v) => field.onChange(v === PARENT_NONE ? null : v)}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="sortOrder">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                label="Sort order"
                value={field.value ?? 100}
                onValueChange={(v) => field.onChange(v ?? 0)}
                min={0}
                max={9999}
                step={10}
                hint="Lower values appear first in lists."
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          <UzFormField name="name">
            {({ field, error }) => (
              <UzInput {...field} label="Display name" error={error} required disabled={pending} />
            )}
          </UzFormField>
          <UzFormField name="description">
            {({ field, error }) => (
              <UzTextarea
                {...field}
                value={field.value ?? ''}
                label="Description"
                rows={3}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="parentRoleId">
            {({ field, error }) => (
              <UzSelect<string>
                name={field.name}
                label="Parent role"
                options={parentOptions}
                value={field.value ?? PARENT_NONE}
                onValueChange={(v) => field.onChange(v === PARENT_NONE ? null : v)}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="sortOrder">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                label="Sort order"
                value={field.value ?? 0}
                onValueChange={(v) => field.onChange(v ?? 0)}
                min={0}
                max={9999}
                step={10}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </UzForm>
      )}
    </UzDrawer>
  );
};
